import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  MAX_QTY,
  cartCreate,
  cartLinesAdd,
  cartLinesRemove,
  cartLinesUpdate,
  cartQuery,
} from "../lib/shopify";

const STORAGE_KEY = "montiway_cart_id";

const readStoredId = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStoredId = (id) => {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // storage unavailable (private mode etc.) - cart lives in memory only
  }
};

const initialState = { cart: null, isOpen: false, isLoading: false, error: null };

const reducer = (state, action) => {
  switch (action.type) {
    case "loading":
      return { ...state, isLoading: true, error: null };
    case "cart":
      return { ...state, cart: action.cart, isLoading: false };
    case "error":
      return { ...state, isLoading: false, error: action.error };
    case "open":
      return { ...state, isOpen: true };
    case "close":
      return { ...state, isOpen: false };
    default:
      return state;
  }
};

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const cartRef = useRef(null);
  cartRef.current = state.cart;

  const setCart = useCallback((cart) => {
    writeStoredId(cart?.id);
    dispatch({ type: "cart", cart });
  }, []);

  // restore cart on mount
  useEffect(() => {
    const storedId = readStoredId();
    if (!storedId) return;
    let cancelled = false;
    cartQuery(storedId)
      .then((cart) => {
        if (cancelled) return;
        // null = cart completed or expired
        setCart(cart);
      })
      .catch(() => {
        // keep the stored id; a transient failure should not lose the cart
      });
    return () => {
      cancelled = true;
    };
  }, [setCart]);

  const run = useCallback(async (fn) => {
    dispatch({ type: "loading" });
    try {
      const cart = await fn();
      setCart(cart);
      return cart;
    } catch (error) {
      dispatch({ type: "error", error });
      throw error;
    }
  }, [setCart]);

  const addToCart = useCallback(
    (variantId, quantity = 1) =>
      run(async () => {
        const current = cartRef.current;
        if (current) {
          const existing = current.lines.find((l) => l.merchandise?.id === variantId);
          try {
            if (existing) {
              return await cartLinesUpdate(current.id, [
                {
                  id: existing.id,
                  quantity: Math.min(MAX_QTY, existing.quantity + quantity),
                },
              ]);
            }
            return await cartLinesAdd(current.id, [
              { merchandiseId: variantId, quantity: Math.min(MAX_QTY, quantity) },
            ]);
          } catch (e) {
            // cart may have been completed/expired - fall through to a new one
            const stale = await cartQuery(current.id).catch(() => undefined);
            if (stale !== null) throw e;
          }
        }
        return cartCreate([
          { merchandiseId: variantId, quantity: Math.min(MAX_QTY, quantity) },
        ]);
      }),
    [run],
  );

  const updateLine = useCallback(
    (lineId, quantity) =>
      run(() => {
        const cartId = cartRef.current?.id;
        if (quantity <= 0) return cartLinesRemove(cartId, [lineId]);
        return cartLinesUpdate(cartId, [
          { id: lineId, quantity: Math.min(MAX_QTY, quantity) },
        ]);
      }),
    [run],
  );

  const removeLine = useCallback(
    (lineId) => run(() => cartLinesRemove(cartRef.current?.id, [lineId])),
    [run],
  );

  const openCart = useCallback(() => dispatch({ type: "open" }), []);
  const closeCart = useCallback(() => dispatch({ type: "close" }), []);

  const value = useMemo(
    () => ({
      ...state,
      addToCart,
      updateLine,
      removeLine,
      openCart,
      closeCart,
    }),
    [state, addToCart, updateLine, removeLine, openCart, closeCart],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
